import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { forField, type Problem } from "../../lib/employment";

/**
 * The field primitives the employee and contractor editors share (WP-15 write screens).
 *
 * Every input renders its own problems underneath itself, worst first, translated from the code the rules return
 * (`emp.problem.<code>`) rather than from a sentence the server sent — so the same rule reads correctly in French
 * and Lingala, and a rule added later needs one i18n key rather than a new endpoint response.
 */

export function Section({ title, hint, children, hook }: { title: string; hint?: string; children: ReactNode; hook?: string }) {
  return (
    <section className="rounded-lg border border-brand-grey-light bg-white p-4" data-person-section={hook}>
      <h2 className="font-heading text-base font-semibold">{title}</h2>
      {hint && <p className="mt-0.5 text-[11px] text-brand-grey">{hint}</p>}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function Problems({ problems }: { problems: Problem[] }) {
  const { t } = useTranslation();
  if (problems.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {problems.map((p) => (
        <p key={p.code} data-person-problem={p.code} data-person-problem-severity={p.severity}
          className={`text-[11px] ${p.severity === "error" ? "text-status-red" : "text-status-amber"}`}>
          {t(`emp.problem.${p.code}`, (p.params ?? {}) as Record<string, unknown>)}
        </p>
      ))}
    </div>
  );
}

const labelClass = "block text-[11px] font-semibold uppercase tracking-wide text-brand-grey";
const controlClass = (bad: boolean) =>
  `mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${bad ? "border-status-red focus:border-status-red" : "border-brand-grey-light focus:border-brand-primary"}`;

type BaseProps = { field: string; label: string; problems: Problem[]; hint?: string; required?: boolean; span?: boolean };

function Wrapper({ field, label, required, hint, mine, span, children }: BaseProps & { mine: Problem[]; children: ReactNode }) {
  return (
    <label className={span ? "sm:col-span-2 lg:col-span-3" : undefined} data-person-field={field}>
      <span className={labelClass}>{label}{required && <span className="ml-0.5 text-status-red">*</span>}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-brand-grey">{hint}</span>}
      <Problems problems={mine} />
    </label>
  );
}

export function TextField({ value, onChange, type = "text", ...base }: BaseProps & {
  value: string; onChange: (v: string) => void; type?: "text" | "date" | "email" | "tel" | "number";
}) {
  const mine = forField(base.problems, base.field);
  const bad = mine.some((p) => p.severity === "error");
  return (
    <Wrapper {...base} mine={mine}>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-person-input={base.field} className={controlClass(bad)} />
    </Wrapper>
  );
}

export function AreaField({ value, onChange, ...base }: BaseProps & { value: string; onChange: (v: string) => void }) {
  const mine = forField(base.problems, base.field);
  return (
    <Wrapper {...base} mine={mine} span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2}
        data-person-input={base.field} className={controlClass(mine.some((p) => p.severity === "error"))} />
    </Wrapper>
  );
}

export function PickField({ value, onChange, options, blank, ...base }: BaseProps & {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; blank?: string;
}) {
  const mine = forField(base.problems, base.field);
  const bad = mine.some((p) => p.severity === "error");
  return (
    <Wrapper {...base} mine={mine}>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-person-input={base.field}
        className={`${controlClass(bad)} bg-white`}>
        <option value="">{blank ?? "—"}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Wrapper>
  );
}
